import Phaser from 'phaser';
import { SeededRng } from '@/core/rng';

/**
 * DEV PLACEHOLDER textures for effects (procedural, labelled `devfx-*`). Final VFX art is produced by
 * the vfx asset builder later; nothing here is shipping geometry.
 */
export const FxTex = { glow: 'devfx-glow', noise: 'devfx-noise', leaf: 'devfx-leaf', drop: 'devfx-drop', spark: 'devfx-spark' } as const;

export function ensureFxTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  if (!tm.exists(FxTex.glow)) {
    // stepped radial rings (pixel-art friendly light pool), 64x64, drawn as a 3/4-view ellipse
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    const rings: [number, number][] = [[32, 0.10], [26, 0.16], [20, 0.24], [14, 0.34], [8, 0.5]];
    for (const [r, a] of rings) { g.fillStyle(0xffffff, a); g.fillEllipse(32, 32, r * 2, r * 1.5); }
    g.generateTexture(FxTex.glow, 64, 64);
    g.destroy();
  }
  if (!tm.exists(FxTex.leaf)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0x8ec95a, 1); g.fillRect(0, 0, 3, 2); g.fillStyle(0x3c7a3a, 1); g.fillRect(2, 0, 1, 1);
    g.fillStyle(0x3c7a3a, 1); g.fillRect(4, 0, 3, 2); g.fillStyle(0x8ec95a, 1); g.fillRect(4, 1, 1, 1);
    g.generateTexture(FxTex.leaf, 7, 2);
    tm.get(FxTex.leaf).add('a', 0, 0, 0, 3, 2); tm.get(FxTex.leaf).add('b', 0, 4, 0, 3, 2);
    g.destroy();
  }
  if (!tm.exists(FxTex.drop)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0xffffff, 1); g.fillRect(0, 0, 1, 8);
    g.generateTexture(FxTex.drop, 1, 8);
    g.destroy();
  }
  if (!tm.exists(FxTex.spark)) {
    const g = scene.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(0xffffff, 1); g.fillRect(0, 0, 2, 2);
    g.generateTexture(FxTex.spark, 2, 2);
    g.destroy();
  }
  if (!tm.exists(FxTex.noise)) {
    // seeded value-noise displacement map (mid grey = no displacement)
    try {
      const size = 64; const canvas = tm.createCanvas(FxTex.noise, size, size);
      if (canvas) {
        const rng = new SeededRng('devfx-noise'); const c = canvas.getContext(); const img = c.createImageData(size, size);
        const cells = 8; const grid: number[] = []; for (let i = 0; i < (cells + 1) * (cells + 1); i++) grid.push(rng.next());
        const smooth = (t: number) => t * t * (3 - 2 * t);
        for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
          const gx = (x / size) * cells, gy = (y / size) * cells; const x0 = Math.floor(gx), y0 = Math.floor(gy); const fx = smooth(gx - x0), fy = smooth(gy - y0);
          const v = (i: number, j: number) => grid[((j % (cells + 1)) * (cells + 1)) + (i % (cells + 1))] ?? 0.5;
          const n = (v(x0, y0) * (1 - fx) + v(x0 + 1, y0) * fx) * (1 - fy) + (v(x0, y0 + 1) * (1 - fx) + v(x0 + 1, y0 + 1) * fx) * fy;
          const o = (y * size + x) * 4; const g8 = Math.round(96 + n * 64); img.data[o] = g8; img.data[o + 1] = 255 - g8; img.data[o + 2] = 128; img.data[o + 3] = 255;
        }
        c.putImageData(img, 0, 0); canvas.refresh();
      }
    } catch (e) { console.warn('[effects] noise texture unavailable', e); }
  }
}
